import { useRef, useState, useEffect } from 'react';
import { resolveImageUrl } from '@/lib/api';

/**
 * SwipeGallery: horizontal scroll-snap gallery.
 *   - Each image shows fully (object-contain on a cream background).
 *   - Touch swipe / mouse drag / arrow buttons.
 *   - Dot indicators at bottom.
 *
 * Props:
 *   images: array of URLs (or /api/uploads/... paths)
 *   aspect: 'square' | 'portrait' | 'landscape' | 'auto'
 *   showArrows: default true on desktop
 *   alt: alt prefix
 */
export default function SwipeGallery({ images = [], aspect = 'portrait', alt = 'photo', className = '' }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const list = (images && images.length ? images : []).map((u) => resolveImageUrl(u));

  const aspectClass =
    aspect === 'square' ? 'aspect-square' :
    aspect === 'landscape' ? 'aspect-[4/3]' :
    aspect === 'auto' ? '' :
    'aspect-[3/4]';

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      setActive(idx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  if (list.length === 0) {
    return <div className={`bg-[color:var(--mb-bg-alt)] ${aspectClass} ${className}`} />;
  }

  return (
    <div className={`relative overflow-hidden bg-[color:var(--mb-bg-alt)] ${aspectClass} ${className}`}>
      <div
        ref={trackRef}
        className="absolute inset-0 flex overflow-x-auto snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        data-testid="swipe-gallery-track"
      >
        <style>{`
          [data-testid="swipe-gallery-track"]::-webkit-scrollbar { display: none; }
        `}</style>
        {list.map((src, i) => (
          <div key={i} className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center">
            <img
              src={src}
              alt={`${alt} ${i + 1}`}
              draggable={false}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="max-w-full max-h-full w-auto h-auto object-contain select-none"
            />
          </div>
        ))}
      </div>

      {list.length > 1 && (
        <>
          {/* Arrows (always visible, semi-transparent) */}
          <button
            type="button"
            onClick={() => scrollTo(active <= 0 ? list.length - 1 : active - 1)}
            aria-label="Previous"
            data-testid="gallery-prev"
            className="flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm border border-[color:var(--mb-border)] shadow-sm hover:bg-white text-[color:var(--mb-text)] text-lg leading-none z-10"
            style={{ transition: 'background-color 200ms ease' }}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => scrollTo(active >= list.length - 1 ? 0 : active + 1)}
            aria-label="Next"
            data-testid="gallery-next"
            className="flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm border border-[color:var(--mb-border)] shadow-sm hover:bg-white text-[color:var(--mb-text)] text-lg leading-none z-10"
            style={{ transition: 'background-color 200ms ease' }}
          >
            ›
          </button>

          {/* Photo-count badge (top-right) */}
          <div className="absolute top-2 right-2 bg-[color:var(--mb-primary)] text-white text-[0.6rem] font-medium tracking-[0.15em] px-2.5 py-1 rounded-full flex items-center gap-1 z-10 shadow-sm">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><path d="m21 15-5-5L5 21"/></svg>
            {active + 1}/{list.length}
          </div>

          {/* "Swipe for more" hint (bottom) */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-full shadow-sm">
              {list.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollTo(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  data-testid={`gallery-dot-${i}`}
                  className={`h-1.5 rounded-full ${i === active ? 'w-5 bg-[color:var(--mb-primary)]' : 'w-1.5 bg-[color:var(--mb-text)]/30'}`}
                  style={{ transition: 'width 250ms ease, background-color 250ms ease' }}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
